import get from "../../utils/get";
import _ from "lodash";
import React from "react";
import { DeepMap, FieldError, Controller, Validate } from "react-hook-form";
import Select, { ActionMeta } from "react-select";
import { v4 as uuidv4 } from "uuid";

interface Props {
  title?: string;
  placeholder: string;
  className?: string;
  options: { value: string | number; label: string }[];
  errors?: DeepMap<Record<string, unknown>, FieldError>;
  required: boolean;
  name: string;
  validate: Validate<any> | Record<string, Validate<any>> | undefined;
  onCreateOption?: any;
  control: any;
  disabled?: boolean;
  clearable?: boolean;
}

export default function InputSearchableSelect({
  title,
  disabled,
  placeholder,
  className,
  options,
  errors,
  required,
  name,
  validate,
  control,
  clearable = true,
}: Props): JSX.Element {
  const formInputId = React.useMemo(() => {
    return uuidv4();
  }, []);

  const error = errors && get(errors, name);

  return (
    <div className="flex flex-col">
      {title ? (
        <label htmlFor={formInputId} className="mb-1 block text-sm font-medium leading-5 text-gray-700">
          {title}
          {error ? error.message ? <span className="text-red-500 "> {error.message}</span> : <span className="text-red-500 "> required</span> : null}
        </label>
      ) : null}
      <Controller
        control={control}
        name={name}
        rules={{ required: required, validate: validate }}
        render={({ field: { onChange, value: val } }) => (
          <>
            <div data-test="creatable-select" className="remove-input-txt-border">
              <Select
                isClearable={clearable}
                isDisabled={disabled}
                placeholder={placeholder}
                onChange={(newValue: any, actionMeta: ActionMeta<any>) => {
                  if (newValue) {
                    onChange(newValue.value);
                    return;
                  }
                  onChange(null);
                  return;
                }}
                value={options.find(({ value }) => value === val) ?? { value: val, label: val }}
                options={options}
                styles={{
                  control: (styles) => ({
                    ...styles,
                    borderRadius: "0.125rem",
                    borderColor: "rgb(209, 213, 219)",
                    ":hover": {
                      borderColor: "rgb(209, 213, 219)",
                      cursor: "pointer",
                    },
                    ":focus-within": {
                      cursor: "text",
                      borderColor: "rgb(147, 197, 253)",
                      boxShadow: "0 0 0 3px rgba(191, 219, 254, .5)",
                    },
                    height: "100%",
                  }),
                  option: (styles) => ({
                    ...styles,
                    cursor: "pointer",
                  }),
                  menu: (styles) => ({
                    ...styles,
                    minWidth: "max-content",
                  }),
                  dropdownIndicator: (styles) => ({
                    ...styles,
                    cursor: "pointer",
                  }),
                  clearIndicator: (styles) => ({
                    ...styles,
                    cursor: "pointer",
                  }),
                }}
                className={`
                ${className}
                text-sm leading-5 rounded
                ${error ? "border-red-200 outline-none ring ring-red-200 ring-opacity-50" : ""}`}
              />
            </div>
          </>
        )}
      />
    </div>
  );
}
